import {
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { bottomTabItems, isRouteActive, MENU_TAB } from '../model/nav-config';

const BUTTON_SIZE = 48; // 탭 한 칸(정사각) 기준 px — 높이 + 최소 가로
const MAX_TAB_WIDTH = BUTTON_SIZE * 1.5; // 가로 최대(라벨이 길면 여기까지 확장)
const WRAP_PADDING = 5; // pill 안쪽 여백 px
const DRAG_THRESHOLD = 6; // 탭 vs 드래그 구분 임계 px
const FOLLOW_SMOOTHING = 0.07; // 드래그 추종 lerp 계수(0~1, 작을수록 더 천천히·끈끈하게 트레일)

// 동적 픽셀값(BUTTON_SIZE 파생)은 인라인 style — Tailwind JIT 는 빌드 때 소스의 *리터럴* 클래스만
// 스캔하므로 `w-[${BUTTON_SIZE}px]` 같은 런타임 템플릿 arbitrary 클래스는 생성되지 않는다.
// 가로만 가변(min=정사각, max=1.5배), 세로는 고정. flex-1(아래)로 wrap 폭을 나눠 가지므로
// 화면이 넓어지면 버튼도 커지고, max 에서 멈춘다(= wrap max 도달 지점).
// touchAction:none — touch-action 은 상속 안 되므로 포인터가 시작되는 자식 탭에도 직접 줘야
// 가로 드래그가 스크롤 제스처로 가로채여 pointercancel 나는 걸 막는다(nav 만으론 부족).
const tabStyle = {
  minWidth: BUTTON_SIZE,
  maxWidth: MAX_TAB_WIDTH,
  height: BUTTON_SIZE,
  touchAction: 'none',
} as const;

const tabClass = (active: boolean) =>
  `relative z-10 flex flex-1 flex-col items-center justify-center px-2 text-[10px] transition-colors ${
    active ? 'text-primary' : 'text-foreground'
  }`;

/**
 * 모바일(< lg) 하단 탭 바 — 아이콘 size-6 + 짧은 라벨(`shortLabel ?? label`; `aria-label`은 풀라벨).
 * 5개 leaf 탭(라우트 `Link`) + 끝에 "메뉴" 탭(라우트 아님 — `onOpenMenu` 로 전체 네비 드로어 토글). safe-area-bottom 흡수.
 * 탭 구성은 nav-config SSOT(`bottomTabItems`). **네이티브 앱 + compact 에서만 마운트** — 데스크탑은 좌측
 * 사이드바가, 브라우저 모바일은 `AppHeader` 좌측 햄버거가 대체한다(게이팅은 `AppShell` 소유).
 *
 * liquid-glass 인디케이터 — 활성 배경을 버튼마다 칠하지 않고 **단일 하이라이트 1개**가 활성 탭의 실제
 * 위치·너비를 측정(`offsetLeft/offsetWidth`)해 `translateX`+`width` 로 슬라이드(springy easing).
 * Pointer 드래그(터치/마우스 공통) 시 손가락을 즉시 추종하고, 떼면 그 위치 탭으로 스냅+이동. 단순 탭은
 * 임계값(`DRAG_THRESHOLD`) 미만이라 네이티브 click(`Link`/`button`)이 그대로 처리.
 */
export function AppBottomNav({
  onOpenMenu,
  menuOpen,
}: {
  onOpenMenu: () => void;
  menuOpen: boolean;
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const tabs = bottomTabItems();
  const tabCount = tabs.length + 1; // 메뉴 탭 포함
  // 메뉴 탭은 leaf 들 뒤(index = tabs.length). 활성 없으면 -1 → 하이라이트 숨김.
  const activeIndex = menuOpen
    ? tabs.length
    : tabs.findIndex((tab) => isRouteActive(tab.route, pathname));
  // wrap 폭은 화면(`100% - 2rem`)을 채우되 [min, max] 로 클램프.
  //  - max: 전체 버튼이 1.5배일 때의 폭 → 넓은 화면에서 버튼이 1.5배에서 멈춤.
  //  - min: 전체 버튼이 정사각(min)일 때의 폭 → 좁은 화면에서 더 줄지 않아 라벨이 안 잘림.
  const wrapMaxWidth = tabCount * MAX_TAB_WIDTH + WRAP_PADDING * 2;
  const wrapMinWidth = tabCount * BUTTON_SIZE + WRAP_PADDING * 2;

  const navRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<Array<HTMLElement | null>>([]);
  const [highlight, setHighlight] = useState<{ x: number; y: number; w: number } | null>(null);
  // 드래그 중 하이라이트 위치(측정값 override). null = 비드래그(측정값 사용).
  const [drag, setDrag] = useState<{ x: number; w: number } | null>(null);
  const [dragging, setDragging] = useState(false); // 추종 중엔 CSS transition off(rAF lerp 가 부드러움 담당)
  const [pressed, setPressed] = useState(false); // 터치/마우스 다운 시 살짝 확대(드래그 시작 시 해제 — 측정 좌표 보존)
  // 제스처 부기 — 위치 외 값은 ref(불필요 리렌더 방지). didDrag = 드래그 발생(뒤따르는 click 억제용).
  const gestureRef = useRef<{ id: number; startX: number; moved: boolean; w: number } | null>(null);
  const didDragRef = useRef(false);
  // rAF 추종 상태 — target(손가락) 으로 current 를 매 프레임 lerp. raf 루프가 setDrag 로 위치 반영.
  const followRef = useRef<{ target: number; current: number; w: number; raf: number } | null>(
    null,
  );

  // 활성 탭의 실제 box 를 읽어 하이라이트 위치·너비 산출(paint 전 = 깜빡임 없음). offsetLeft/Top 은
  // offsetParent(nav)의 padding edge 기준이라, 하이라이트를 left:0/top:0 에 두고 그대로 translate 하면
  // 정확히 정렬된다. 폰트 로드·뷰포트 변화로 탭 폭이 바뀌면 ResizeObserver 가 재측정. 라우트/활성이
  // 바뀌면 드래그 override 를 해제(측정값으로 정착).
  useLayoutEffect(() => {
    const measure = () => {
      const el = activeIndex >= 0 ? tabRefs.current[activeIndex] : null;
      setHighlight(el ? { x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth } : null);
    };
    // 활성/라우트 변화 시 측정 + 드래그 override 해제(측정값으로 정착). ResizeObserver 는 measure 만.
    const settle = () => {
      measure();
      setDrag(null);
    };
    settle();
    const nav = navRef.current;
    if (!nav) return;
    const ro = new ResizeObserver(measure);
    ro.observe(nav);
    return () => ro.disconnect();
  }, [activeIndex, pathname]);

  // 언마운트 중 rAF 누수 방지.
  useEffect(
    () => () => {
      const f = followRef.current;
      if (f) cancelAnimationFrame(f.raf);
    },
    [],
  );

  // rAF lerp 루프 — current 를 target 쪽으로 매 프레임 당기고 setDrag 로 반영(거의 도달하면 스냅 종료 없이 유지).
  const startFollow = () => {
    const tick = () => {
      const f = followRef.current;
      if (!f) return;
      f.current += (f.target - f.current) * FOLLOW_SMOOTHING;
      if (Math.abs(f.target - f.current) < 0.3) f.current = f.target;
      setDrag({ x: f.current, w: f.w });
      f.raf = requestAnimationFrame(tick);
    };
    if (followRef.current) followRef.current.raf = requestAnimationFrame(tick);
  };

  const stopFollow = () => {
    const f = followRef.current;
    if (f) cancelAnimationFrame(f.raf);
    followRef.current = null;
  };

  // clientX → offset 좌표(하이라이트/탭과 같은 기준). press 확대(scale)가 걸려 있으면 client px ≠ layout px
  // 이므로, nav 의 실제 scale(rect.width/offsetWidth)로 나눠 보정. tab0 를 기준점으로 환산. scale=1 이면 그대로.
  const clientToOffsetX = (clientX: number) => {
    const t0 = tabRefs.current[0];
    const nav = navRef.current;
    if (!t0 || !nav) return 0;
    const scale = nav.getBoundingClientRect().width / nav.offsetWidth || 1;
    return t0.offsetLeft + (clientX - t0.getBoundingClientRect().left) / scale;
  };

  // offset 좌표가 속한 탭 인덱스(끝 = 메뉴).
  const indexAtOffsetX = (offX: number) => {
    for (let i = 0; i < tabCount; i++) {
      const el = tabRefs.current[i];
      if (el && offX < el.offsetLeft + el.offsetWidth) return i;
    }
    return tabCount - 1;
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (!e.isPrimary) return;
    didDragRef.current = false;
    gestureRef.current = {
      id: e.pointerId,
      startX: e.clientX,
      moved: false,
      w: highlight?.w ?? BUTTON_SIZE,
    };
    setPressed(true);
    // capture 는 드래그가 실제 시작될 때만(onPointerMove) — 다운 즉시 잡으면 포인터 캡처 규칙상
    // click 이 캡처 요소(nav)로 디스패치돼 Link 의 라우팅 onClick 이 안 터진다(마우스 클릭 먹통).
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLElement>) => {
    const g = gestureRef.current;
    if (!g || e.pointerId !== g.id) return;
    if (!g.moved && Math.abs(e.clientX - g.startX) < DRAG_THRESHOLD) return;
    e.preventDefault();
    const first = tabRefs.current[0];
    const last = tabRefs.current[tabCount - 1];
    const minX = first ? first.offsetLeft : WRAP_PADDING;
    const maxX = last ? last.offsetLeft + last.offsetWidth - g.w : minX;
    const x = Math.min(Math.max(clientToOffsetX(e.clientX) - g.w / 2, minX), maxX);
    if (!g.moved) {
      // 첫 드래그 — 손가락이 nav 밖으로 나가도 추적하도록 이때 capture(클릭 충돌 없음, 드래그뿐).
      // 현재 하이라이트 위치에서 손가락으로 lerp 시작.
      g.moved = true;
      didDragRef.current = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      followRef.current = { target: x, current: highlight?.x ?? x, w: g.w, raf: 0 };
      setDragging(true);
      startFollow();
    } else if (followRef.current) {
      followRef.current.target = x; // 목표만 갱신(rAF 루프가 추종)
    }
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLElement>) => {
    const g = gestureRef.current;
    gestureRef.current = null;
    if (!g || e.pointerId !== g.id) return;
    stopFollow();
    setDragging(false);
    setPressed(false);
    if (!g.moved) return; // 단순 탭 → 네이티브 click 이 처리
    const idx = indexAtOffsetX(clientToOffsetX(e.clientX));
    const willChange = idx === tabs.length ? !menuOpen : idx !== activeIndex;
    if (willChange) {
      // 타깃 슬롯으로 glide(transition on) → 라우트/메뉴 반영 후 layout effect 가 drag 해제.
      const el = tabRefs.current[idx];
      if (el) setDrag({ x: el.offsetLeft, w: el.offsetWidth });
      const target = tabs[idx];
      if (target) navigate(target.route);
      else onOpenMenu();
    } else {
      setDrag(null); // 변화 없음 → 측정값으로 정착
    }
  };

  const onPointerCancel = () => {
    gestureRef.current = null;
    stopFollow();
    setDragging(false);
    setPressed(false);
    setDrag(null);
  };

  // 드래그 직후 뒤따르는 click(Link/button) 억제 — 이미 pointerup 에서 이동을 처리했으므로 중복 방지.
  const onClickCapture = (e: ReactMouseEvent<HTMLElement>) => {
    if (!didDragRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    didDragRef.current = false;
  };

  const hl = drag ?? highlight;

  return (
    <nav
      ref={navRef}
      // 좌우 1rem 여백으로 화면 폭을 채우고([min,max] 클램프), left-1/2 + translateX(-50%) 로 중앙정렬.
      // 중앙정렬·press 확대를 한 transform 으로 합쳐야 해서 translate 는 인라인(class 와 합성 회피).
      className={`fixed left-1/2 z-30 flex select-none border border-white/50 dark:border-gray-700/30 bg-linear-to-br from-gray-300/25 to-gray-300/30 dark:from-gray-300/10 dark:to-gray-300/15 backdrop-blur-sm shadow-[0_3px_10px_rgba(0,0,0,0.1)] transition-all duration-500 ease-out  ${pressed && 'shadow-[0_5px_20px_rgba(0,0,0,0.1)]'}`}
      // 바닥 간격 = max(0.75rem, safe-area) — 홈 인디케이터 기기는 inset 만큼(그 위에 상수 안 더함), 없는
      // 기기는 최소 0.75rem 확보 → 기기 간 뜨는 높이 균일. touch-action:none = 드래그 시 스크롤 가로채기 방지.
      style={{
        bottom: 'max(0.75rem, env(safe-area-inset-bottom))',
        width: 'calc(100% - 2rem)',
        minWidth: wrapMinWidth,
        maxWidth: wrapMaxWidth,
        padding: WRAP_PADDING,
        borderRadius: BUTTON_SIZE / 2 + WRAP_PADDING,
        touchAction: 'none',
        transformOrigin: 'bottom center',
        transform: pressed ? 'translateX(-50%) scale(1.03)' : 'translateX(-50%)',
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onClickCapture={onClickCapture}
    >
      {/* 활성 탭/드래그를 따라다니는 단일 하이라이트 — 추종 중엔 transition off(rAF lerp 트레일), 정착 시 liquid 바운스. */}
      <span
        aria-hidden
        className={`pointer-events-none absolute left-0 top-0 bg-linear-to-br from-gray-300/30 to-gray-300/35 ${
          dragging
            ? ''
            : 'transition-[transform,width,opacity] duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]'
        }`}
        style={{
          height: BUTTON_SIZE,
          width: hl?.w ?? BUTTON_SIZE,
          borderRadius: BUTTON_SIZE / 2,
          transform: `translate(${hl?.x ?? 0}px, ${highlight?.y ?? WRAP_PADDING}px)`,
          opacity: hl ? 1 : 0,
        }}
      />
      {tabs.map((tab, i) => {
        const active = isRouteActive(tab.route, pathname);
        return (
          <Link
            key={tab.key}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            to={tab.route}
            aria-label={tab.label}
            aria-current={active ? 'page' : undefined}
            className={tabClass(active)}
            style={tabStyle}
            draggable={false}
          >
            <tab.icon className="size-6" />
            <span className="max-w-full truncate">{tab.shortLabel ?? tab.label}</span>
          </Link>
        );
      })}
      <button
        ref={(el) => {
          tabRefs.current[tabs.length] = el;
        }}
        type="button"
        onClick={onOpenMenu}
        aria-label={MENU_TAB.label}
        aria-expanded={menuOpen}
        className={tabClass(menuOpen)}
        style={tabStyle}
      >
        <MENU_TAB.icon className="size-6" />
        <span className="max-w-full truncate">{MENU_TAB.label}</span>
      </button>
    </nav>
  );
}
